import { useMemo, useState } from "react";
import { useRouter } from "next/router";
import { useKeenSlider } from "keen-slider/react";

import { useTranslation } from "@hooks/useTranslation";
import { cn } from "@/lib/utils";
import { VideoSlide } from "@components/fullScreenVideo/VideoSlide";
import { VideoAvatar } from "@components/fullScreenVideo/VideoAvatar";
import { Placeholder } from "@components/fullScreenVideo/Placeholder";
import { UserModal } from "@components/fullScreenVideo/UserModal";
import { VideoTuto } from "@components/fullScreenVideo/VideoTuto";
import { events, track } from "@shared/tracking";
import { type Post } from "./video-slide";

import "keen-slider/keen-slider.min.css";

type VideosSliderProps = {
  experienceId: number;
  title: string;
  posts: Post[];
  section: string;
  isSingleActivity: boolean;
};

export default function VideosSlider({
  experienceId,
  posts,
  section,
  isSingleActivity,
}: VideosSliderProps) {
  const router = useRouter();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [loaded, setLoaded] = useState(false);

  const { t } = useTranslation();

  const [isOpenUserModal, setIsOpenUserModal] = useState(false);
  const [isVideoTutoOpen, setIsVideoTutoOpen] = useState(false);
  const [isPlaceholderOpen, setIsPlaceholderOpen] = useState(false);

  const [drag, setDrag] = useState(true);

  const [ref, instanceRef] = useKeenSlider<HTMLDivElement>({
    loop: true,
    drag,
    slideChanged(slider) {
      setCurrentSlide(slider.track.details.rel);
    },
    created() {
      setLoaded(true);
    },
  });

  const randomHottest = useMemo(() => {
    return Math.floor(Math.random() * 60 + 1);
  }, []);

  return (
    <div className="relative h-full  md:rounded-2xl">
      <div ref={ref} className="keen-slider !h-full md:rounded-2xl">
        {posts.map((post, idx) => (
          <div
            key={idx}
            className="keen-slider__slide h-screen w-full md:rounded-2xl"
          >
            <VideoSlide
              videoSrc={post.url}
              videoPoster={post.thumbnailUrl}
              playVideo={
                currentSlide === idx &&
                router.query.expand_activities !== "true" &&
                !isVideoTutoOpen &&
                !isOpenUserModal &&
                !isPlaceholderOpen
              }
              isActive={currentSlide === idx}
              onVideoEnded={() => {
                instanceRef.current?.next();
              }}
              onVideoPlay={() => {
                track(events.PLAY_VIDEO_EVENT, {
                  post_id: post.postId.toString(),
                  [section === "experiences"
                    ? "experience_id"
                    : "place_to_visit_id"]: experienceId.toString(),
                  city_id: router.query.id!.toString(),
                  section,
                  ...(router.query.categories && {
                    categories_ids: router.query.categories as string,
                  }),
                  ...(router.query.sortOption && {
                    sort_by: router.query.sortOption as string,
                  }),
                });
              }}
              isSingleActivity={isSingleActivity}
              onSlideStart={() => setDrag(false)}
              onSlideEnd={() => setDrag(true)}
            />
          </div>
        ))}
        {loaded && instanceRef.current && (
          <div className="absolute top-14 z-50 w-full flex place-content-center mt-0.5 gap-1 cursor-pointer">
            {Array.from({ length: posts.length }).map((_, idx) => {
              return (
                <button
                  key={idx}
                  onClick={() => {
                    instanceRef.current?.moveToIdx(idx);
                  }}
                  className={cn(
                    "h-2 rounded-full transition-all duration-300",
                    currentSlide === idx
                      ? "bg-primary-color w-3"
                      : "bg-gray-300/40 w-2"
                  )}
                ></button>
              );
            })}
          </div>
        )}
      </div>
      <UserModal
        userDetails={{
          userId: posts[currentSlide].userId,
          username: posts[currentSlide].username,
          userThumbnail: posts[currentSlide].userThumbnail,
        }}
        isOpenUserModal={isOpenUserModal}
        setIsOpenUserModal={setIsOpenUserModal}
      />

      <div className="absolute z-50 flex justify-between w-full px-1 top-[4.5rem]">
        <button
          onClick={(evt) => {
            evt.stopPropagation();
            setIsOpenUserModal(true);
          }}
          className="flex items-center w-40 h-full"
        >
          <VideoAvatar
            username={posts[currentSlide].username}
            thumbnail={posts[currentSlide].userThumbnail}
          />
        </button>
        <div className="w-40 text-[11px] text-white h-full grid place-items-center">
          <div className="w-full p-1 text-center rounded-xl bg-black/50">
            <span className=" text-primary-color">
              {randomHottest}{" "}
              {randomHottest == 1
                ? t("spotprofile.experiences.video.more_person")
                : t("spotprofile.experiences.video.more_people")}{" "}
            </span>
            {t("spotprofile.experiences.video.watching")}
          </div>
        </div>
      </div>

      <VideoTuto
        onOpen={() => {
          setIsVideoTutoOpen(true);
        }}
        onClose={() => {
          setIsVideoTutoOpen(false);
        }}
      />

      {!isVideoTutoOpen && (
        <Placeholder
          onOpen={() => setIsPlaceholderOpen(true)}
          onClose={() => setIsPlaceholderOpen(false)}
        />
      )}
    </div>
  );
}
